import { RemoteParticipant } from "livekit-client";

// ─── Interview Context ────────────────────────────────────────────────────────

/** Interview context from Company Interview Editor (candidate profile, research, etc.) */
export interface InterviewContext {
    title?: string;
    description?: string;
    instructions?: string;
    content?: string;
    difficulty?: string;
    timeLimit?: number;
    allowedLanguages?: string[];
    interviewType?: 'coding' | 'system-design' | 'behavioral' | 'comprehensive';
    candidateName?: string;
    candidateLinkedIn?: string;
    candidateDocuments?: Array<{ name: string; url: string; type?: string }>;
    candidateResearchData?: string;
    agentSystemPrompt?: string | null;
}

// ─── Component Props ──────────────────────────────────────────────────────────

export interface LiveKitAgentProps {
    sessionId?: string;
    darkMode?: boolean;
    codeContent?: string;
    compilerOutput?: string;
    language?: string;
    participantCount?: number;
    userName?: string;
    interviewContext?: InterviewContext | null;
    diagramContent?: string;
}

// ─── Transcript Types ─────────────────────────────────────────────────────────

export interface TranscriptMessage {
    id: string;
    text: string;
    sender: "user" | "agent";
    timestamp: Date;
    segmentId?: string;
    isFinal?: boolean;
}

// ─── Agent Metrics ────────────────────────────────────────────────────────────

export interface AgentMetrics {
    overallLatency?: number;
    llmLatency?: number;
    ttsLatency?: number;
    metricType?: string;
}

export interface MetricsHistoryEntry {
    timestamp: number;
    overall?: number;
    llm?: number;
    tts?: number;
}

// ─── Interview Scores ─────────────────────────────────────────────────────────

export interface InterviewScores {
    problemUnderstanding?: number;
    approach?: number;
    implementation?: number;
    complexityAnalysis?: number;
    communication?: number;
    recovery?: number;
    requirementsClarification?: number;
    architectureDesign?: number;
    scalability?: number;
    tradeOffs?: number;
    diagramQuality?: number;
    overall: number;
    summary: string;
    [key: string]: number | string | undefined;
}

// ─── Agent Identity Detection ─────────────────────────────────────────────────

/** Agent name constant — update this if the agent name changes */
export const AGENT_NAME = "Alex";
const AGENT_IDENTITY_KEYWORDS = ["agent", AGENT_NAME];

/** Check if a participant is the AI agent by identity or name */
export const isAgentParticipant = (
    participant: { identity?: string; name?: string | null } | null | undefined
): boolean => {
    if (!participant) return false;
    const identity = participant.identity?.toLowerCase() || "";
    const name = participant.name?.toLowerCase() || "";
    return AGENT_IDENTITY_KEYWORDS.some((kw) => {
        const lower = kw.toLowerCase();
        return identity.includes(lower) || name.includes(lower);
    });
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

export const getTranscriptStorageKey = (sessionId?: string) =>
    `livekit_agent_transcript_${sessionId || "default"}`;
